import { useState, useEffect } from 'react';
import { logger } from '~/logger/logger';
import { isbot } from 'isbot';

/**
 * Custom hook to determine if the Assembled script has loaded and is ready.
 * It listens for the 'assembledload' event, then checks `window.assembled.isReady()`.
 * It also re-evaluates if the presence of `window.assembled.isReady` changes across renders.
 *
 * @returns {boolean} True if Assembled is ready, false otherwise.
 */
export function useIsAssembledReady(): boolean {
  const [assembledReady, setAssembledReady] = useState(false);
  const [hasLoadEventFired, setHasLoadEventFired] = useState(false);

  const isBrowser = typeof window !== 'undefined';
  const shouldShortCircuit = !isBrowser;

  // Listen to the assembledload event to determine if the Assembled script has loaded
  useEffect(() => {
    // Evaluate isbot in effect to avoid SSR issues where navigator is undefined
    if (shouldShortCircuit || isbot(navigator.userAgent || '')) return;

    const handleAssembledLoad = () => {
      setHasLoadEventFired(true);
    };

    window.addEventListener('assembledload', handleAssembledLoad);

    return () => {
      if (isBrowser) {
        window.removeEventListener('assembledload', handleAssembledLoad);
      }
    };
  }, [shouldShortCircuit, isBrowser]);

  // If the Assembled script has loaded, check if it's ready
  useEffect(() => {
    if (shouldShortCircuit || !hasLoadEventFired) return;

    setAssembledReady(false);

    const isReadyFunctionAvailable =
      isBrowser && typeof window.assembled?.isReady === 'function';

    if (!isReadyFunctionAvailable) return;

    try {
      const ready =
        typeof window.assembled?.isReady === 'function' && window.assembled.isReady();
      setAssembledReady(ready);
    } catch (error) {
      logger.error('Error calling window.assembled.isReady():', error);
    }
  }, [shouldShortCircuit, hasLoadEventFired, isBrowser]);

  if (shouldShortCircuit) {
    return false;
  }

  return assembledReady;
}
